/**
 * Phase 6 plan 06-05 Task 1 — staff session lib (RED-03 / D-05).
 *
 * Plain functions over `sessionStorage['mf_staff_jwt']` + `['mf_staff_slug']`.
 * Analog: `useMerchantSession.ts` claims-shape + the edge-fn `verifyStaff` exp
 * logic (`exp * 1000 < Date.now()` = expired).
 *
 * Why sessionStorage (NOT localStorage) — D-05: the 12h staff JWT must DIE on
 * tab close (the counter device is physically controlled but a closed tab should
 * force a re-PIN). localStorage would survive a browser restart; sessionStorage
 * is scoped to the tab session, which is the intended lifetime.
 *
 * Why a client-side base64url decode WITHOUT signature verification: the three
 * staff edge fns (peek-reward / redeem / cafe-today) re-verify the JWT signature
 * server-side via `verifyStaff` (Plan 03). The client read is defense-in-depth
 * UX only — it lets the guards short-circuit an obviously-expired token before a
 * round-trip. A tampered client JWT still fails server verification, so trusting
 * the client-decoded `exp` is safe (worst case: a forged future-exp token gets
 * one rejected server call, then the 401 guard routes to login). Same posture as
 * the note in `useMerchantSession.ts`.
 *
 * D-05 slug persistence: staff JWT claims carry only `venue_id` (NO slug), and
 * `/cafe/:slug` resolves by SLUG — so the login slug must be persisted
 * separately so the scan/today guards can route back to the right login page
 * (F5: never a bare unregistered `/cafe`).
 *
 * SSR-safe: guards `typeof window` even though the SPA has no SSR (tests run in
 * node; the jsdom env supplies sessionStorage, but a node-env import must not
 * throw at module load).
 */

const JWT_KEY = "mf_staff_jwt";
const SLUG_KEY = "mf_staff_slug";

/** Decoded staff JWT claims (the subset the client reads). */
export interface StaffClaims {
  venue_id: string;
  exp: number;
}

/** True when sessionStorage is unavailable (SSR / node without jsdom). */
function noStorage(): boolean {
  return typeof window === "undefined" || typeof window.sessionStorage === "undefined";
}

/**
 * base64url-decode the JWT middle segment and JSON.parse it. Returns null on a
 * malformed/absent token. Never throws (callers treat null as logged-out).
 */
function decodeClaims(token: string | null): StaffClaims | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    // base64url → base64, then atob → JSON.
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    // Re-pad to a multiple of 4 (atob requires canonical padding).
    while (b64.length % 4 !== 0) b64 += "=";
    const json = atob(b64);
    const parsed = JSON.parse(json) as Partial<StaffClaims>;
    if (typeof parsed.venue_id !== "string" || typeof parsed.exp !== "number") {
      return null;
    }
    return { venue_id: parsed.venue_id, exp: parsed.exp };
  } catch {
    return null;
  }
}

/** Persist the 12h staff JWT (D-05). */
export function setStaffJwt(jwt: string): void {
  if (noStorage()) return;
  window.sessionStorage.setItem(JWT_KEY, jwt);
}

/**
 * The raw staff JWT, or null if absent OR expired. Auto-clears on an expired
 * read so the stale token never lingers in sessionStorage.
 */
export function getStaffJwt(): string | null {
  if (noStorage()) return null;
  const token = window.sessionStorage.getItem(JWT_KEY);
  if (!token) return null;
  if (isStaffExpiredToken(token)) {
    clearStaffJwt();
    return null;
  }
  return token;
}

/**
 * Decode { venue_id, exp } from the stored JWT, or null if absent / malformed /
 * expired. Does NOT verify the signature (the edge fns do — see header).
 */
export function getStaffClaims(): StaffClaims | null {
  if (noStorage()) return null;
  const token = window.sessionStorage.getItem(JWT_KEY);
  const claims = decodeClaims(token);
  if (!claims) return null;
  if (claims.exp * 1000 < Date.now()) return null;
  return claims;
}

/** Internal: expiry check against a specific token. */
function isStaffExpiredToken(token: string): boolean {
  const claims = decodeClaims(token);
  // A malformed/undecodable token is treated as expired (fail-closed: force re-PIN).
  if (!claims) return true;
  return claims.exp * 1000 < Date.now();
}

/**
 * True when the stored staff JWT is expired (mirrors `verifyStaff`:
 * `exp * 1000 < Date.now()`). A missing/malformed token also reads as expired.
 */
export function isStaffExpired(): boolean {
  if (noStorage()) return true;
  const token = window.sessionStorage.getItem(JWT_KEY);
  if (!token) return true;
  return isStaffExpiredToken(token);
}

/** Persist the login slug so the scan/today guards can route back to login (D-05/F5). */
export function setStaffSlug(slug: string): void {
  if (noStorage()) return;
  window.sessionStorage.setItem(SLUG_KEY, slug);
}

/** The persisted login slug, or null if none. */
export function getStaffSlug(): string | null {
  if (noStorage()) return null;
  return window.sessionStorage.getItem(SLUG_KEY);
}

/** Clear BOTH the JWT and the slug (logout / expiry). */
export function clearStaffJwt(): void {
  if (noStorage()) return;
  window.sessionStorage.removeItem(JWT_KEY);
  window.sessionStorage.removeItem(SLUG_KEY);
}
